import React, { useCallback, useRef } from 'react';
import { SiteDisplayInfo } from '../../types/site';
import { EffectivePricing } from '../../services/siteService';
import { formatCurrency } from '../../utils/formatters';

// ============================================================================
// SitesTable - Table view with infinite scroll and sortable columns
// ============================================================================

type SortDirection = 'asc' | 'desc';
type SortKey = 'alias' | 'type_name' | 'section_name' | 'effective_rate' | 'status';

interface SiteWithEffective extends SiteDisplayInfo {
  effectivePricing?: EffectivePricing;
}

interface SitesTableProps {
  sites: SiteWithEffective[];
  onSiteClick: (site: SiteDisplayInfo) => void;
  // Pagination props
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  // Sort props (controlled by parent)
  sortKey: SortKey;
  sortDirection: SortDirection;
  onSort: (key: SortKey) => void;
  allSitesLoaded: boolean;
}

const STATUS_CONFIG: Record<string, { color: string; label: string; icon: string }> = {
  available: { color: '#22c55e', label: 'Available', icon: '🟢' },
  occupied: { color: '#3b82f6', label: 'Occupied', icon: '🔵' },
  maintenance: { color: '#f59e0b', label: 'Maintenance', icon: '🟠' },
  blocked: { color: '#ef4444', label: 'Blocked', icon: '🔴' },
  seasonal: { color: '#8b5cf6', label: 'Seasonal', icon: '🟣' },
};

const SitesTable: React.FC<SitesTableProps> = ({
  sites,
  onSiteClick,
  hasMore,
  loadingMore,
  onLoadMore,
  sortKey,
  sortDirection,
  onSort,
  allSitesLoaded,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle scroll - detect when near bottom
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 200; // 200px threshold

    if (nearBottom && hasMore && !loadingMore) {
      onLoadMore();
    }
  }, [hasMore, loadingMore, onLoadMore]);

  // Render sort indicator
  const renderSortIndicator = (key: SortKey) => {
    if (sortKey !== key) return <span className="sort-indicator">⇅</span>;
    return (
      <span className="sort-indicator active">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Check if rate sorting is disabled (not all sites loaded)
  const isRateSortDisabled = !allSitesLoaded;

  return (
    <div
      className="sites-table-container"
      ref={containerRef}
      onScroll={handleScroll}
      style={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      <table className="sites-table-v2">
        <thead>
          <tr>
            <th className="sortable" onClick={() => onSort('alias')}>
              Alias {renderSortIndicator('alias')}
            </th>
            <th className="sortable" onClick={() => onSort('type_name')}>
              Type {renderSortIndicator('type_name')}
            </th>
            <th className="sortable" onClick={() => onSort('section_name')}>
              Section {renderSortIndicator('section_name')}
            </th>
            <th
              className={`sortable ${isRateSortDisabled ? 'disabled' : ''}`}
              onClick={() => !isRateSortDisabled && onSort('effective_rate')}
              title={isRateSortDisabled ? 'Load all sites to sort by rate' : 'Sort by effective rate'}
            >
              Effective Rate {renderSortIndicator('effective_rate')}
              {isRateSortDisabled && <span className="sort-disabled-hint">*</span>}
            </th>
            <th className="sortable" onClick={() => onSort('status')}>
              Status {renderSortIndicator('status')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sites.map(site => {
            const statusConfig = STATUS_CONFIG[site.status] || STATUS_CONFIG.available;
            const effectivePricing = site.effectivePricing;

            return (
              <tr
                key={site.id}
                onClick={() => onSiteClick(site)}
                className={`site-row ${site.status}`}
              >
                {/* Site Alias */}
                <td className="site-alias-cell">
                  <span className="site-alias-link">{site.alias}</span>
                </td>

                {/* Type */}
                <td>{site.type_name || '-'}</td>

                {/* Section */}
                <td>{site.section_name || '-'}</td>

                {/* Rate with Source */}
                <td className="rate-cell">
                  {effectivePricing ? (
                    <div className="rate-display">
                      <span className="rate-value">
                        {formatCurrency(effectivePricing.price)}/n
                      </span>
                      <span className={`rate-source ${effectivePricing.source}`}>
                        {effectivePricing.source === 'store' ? 'Store' : 'Site'}
                      </span>
                    </div>
                  ) : site.current_price ? (
                    <span className="rate-value">{formatCurrency(site.current_price)}/n</span>
                  ) : (
                    <span className="no-rate">-</span>
                  )}
                </td>

                {/* Status */}
                <td className="status-cell">
                  <span className="status-indicator">
                    <span className="status-icon">{statusConfig.icon}</span>
                    <span className="status-label">{statusConfig.label}</span>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Loading indicator */}
      {loadingMore && (
        <div className="sites-loading-more">
          <div className="loading-spinner-small"></div>
          <span>Loading more sites...</span>
        </div>
      )}

      {/* End of list indicator */}
      {!hasMore && sites.length > 0 && (
        <div className="sites-end-of-list">
          All {sites.length} sites loaded
        </div>
      )}
    </div>
  );
};

export default SitesTable;
